import { useState, useEffect, useMemo } from 'react';
import { FaFolder, FaFilePdf, FaFileAlt, FaTimes, FaArrowLeft, FaChevronRight } from 'react-icons/fa';
import { HiDocumentText } from 'react-icons/hi';
import { FiPaperclip } from "react-icons/fi";
import { GetFileHistory } from '../../../services/GetFileHistory'
import './FileUploadWorkspace.css'

export const FileUploadWorkspace = ({
  onFileSelect,
  triggerLabel = 'Select file from workspace',
  triggerClassName = '',
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPath, setCurrentPath] = useState('/');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'fileType', 'recentlyUsed'

  const fetchFiles = async (path = '/') => {
    setIsLoading(true);
    setError(null);
    
    try {
        const { filesArray } = await GetFileHistory.GetHistory(path);
        
        setFiles(filesArray);
        setCurrentPath(path);
    } catch (err) {
        setError(err.message);
        setFiles([]);
    } finally {
        setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      fetchFiles(currentPath);
      setSearchQuery('');
      setSortBy('newest');
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (isModalOpen) {
      fetchFiles(currentPath);
    }
  }, [currentPath]);

  const getFileExtension = (fileName) => {
    if (!fileName) return '';
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  };

  const getFileType = (file) => {
    if (file.is_folder || file.type === 'folder' || file.is_directory) {
      return 'folder';
    }
    const ext = getFileExtension(file.name || file.filename || '');
    const typeMap = {
      'pdf': 'PDF',
      'doc': 'Word',
      'docx': 'Word',
      'txt': 'Text',
      'html': 'HTML',
      'xls': 'Excel',
      'xlsx': 'Excel',
      'ppt': 'PowerPoint',
      'pptx': 'PowerPoint',
      'jpg': 'Image',
      'jpeg': 'Image',
      'png': 'Image',
      'mp4': 'Video',
      'mp3': 'Audio',
    };
    return typeMap[ext] || 'Other';
  };

  const filteredAndSortedFiles = useMemo(() => {
    let filtered = files;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = files.filter(file => {
        const fileName = (file.name || file.filename || '').toLowerCase();
        return fileName.includes(query);
      });
    }

    const folders = filtered.filter(f => f.is_folder || f.type === 'folder' || f.is_directory);
    const fileItems = filtered.filter(f => !(f.is_folder || f.type === 'folder' || f.is_directory));

    let sortedFileItems = [...fileItems];
    
    switch (sortBy) {
      case 'newest':
        sortedFileItems.sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 
                        a.uploaded_at ? new Date(a.uploaded_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 
                        b.uploaded_at ? new Date(b.uploaded_at).getTime() : 0;
          return dateB - dateA;
        });
        break;
      
      case 'fileType':
        sortedFileItems.sort((a, b) => {
          const typeA = getFileType(a);
          const typeB = getFileType(b);
          if (typeA !== typeB) {
            return typeA.localeCompare(typeB);
          }
          return (a.name || '').localeCompare(b.name || '');
        });
        break;
      
      case 'recentlyUsed':
        sortedFileItems.sort((a, b) => {
          const dateA = a.last_used ? new Date(a.last_used).getTime() : 0;
          const dateB = b.last_used ? new Date(b.last_used).getTime() : 0;
          return dateB - dateA;
        });
        break;
      
      default:
        break;
    }

    return [...folders, ...sortedFileItems];
  }, [files, searchQuery, sortBy]);

  const getFileIcon = (file) => {
    if (file.is_folder || file.type === 'folder' || file.is_directory) {
      return <FaFolder className="file-icon folder-icon" />;
    }
    
    const fileName = file.name || file.filename || '';
    if (fileName.endsWith('.pdf')) {
      return <FaFilePdf className="file-icon pdf-icon" />;
    }
    if (fileName.endsWith('.txt')) {
      return <HiDocumentText className="file-icon txt-icon" />;
    }
    
    return <FaFileAlt className="file-icon default-icon" />;
  };

  const handleItemClick = (file) => {
    if (file.is_folder) {
      const newPath = currentPath === '/' 
        ? `/${file.name}` 
        : `${currentPath}/${file.name}`;
      fetchFiles(newPath);
    } else {
      onFileSelect?.({
        id: file.id,
        name: file.name,
        task_id: file.task_id,
        last_used: file.last_used,
        fromWorkspace: true
      });
      setIsModalOpen(false);
    }
  };

  const handleGoBack = () => {
    if (currentPath === '/') return;
    
    const pathParts = currentPath.split('/').filter(Boolean);
    pathParts.pop();
    const parentPath = pathParts.length > 0 ? `/${pathParts.join('/')}` : '/';
    fetchFiles(parentPath);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    
    if (diffMinutes < 60) {
      return `${diffMinutes} minutes ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hours ago`;
    } else if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    }
  };

  return (
    <>
      <button
        type="button"
        className={`workspace-trigger-button ${triggerClassName}`.trim()}
        onClick={() => setIsModalOpen(true)}
      >
        <FiPaperclip />
        {triggerLabel}
      </button>

      {isModalOpen && (
        <div className="workspace-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="workspace-modal" onClick={(e) => e.stopPropagation()}>
            <div className="workspace-modal-header">
              <h3><FiPaperclip />Select previously uploaded file</h3>
              <button 
                className="workspace-modal-close"
                onClick={() => setIsModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            {currentPath !== '/' && (
              <div className="workspace-breadcrumb">
                <button 
                  className="workspace-back-button"
                  onClick={handleGoBack}
                >
                  <FaArrowLeft />
                  Back
                </button>
                <span className="workspace-current-path">{currentPath}</span>
              </div>
            )}

            <div className="workspace-search-sort">
              <div className="workspace-search-container">
                <input
                  id="workspace-search"
                  type="text"
                  className="workspace-search-input"
                  placeholder="Search files"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <div className="workspace-sort-container">
                <label className="workspace-sort-label">Sort:</label>
                <div className="workspace-sort-buttons">
                  <button
                    className={`workspace-sort-button ${sortBy === 'newest' ? 'workspace-sort-button--active' : ''}`}
                    onClick={() => setSortBy('newest')}
                  >
                    {sortBy === 'newest'}
                    Newest
                  </button>
                  <button
                    className={`workspace-sort-button ${sortBy === 'fileType' ? 'workspace-sort-button--active' : ''}`}
                    onClick={() => setSortBy('fileType')}
                  >
                    {sortBy === 'fileType'}
                    File Type
                  </button>
                  <button
                    className={`workspace-sort-button ${sortBy === 'recentlyUsed' ? 'workspace-sort-button--active' : ''}`}
                    onClick={() => setSortBy('recentlyUsed')}
                  >
                    {sortBy === 'recentlyUsed'}
                    Recently Used
                  </button>
                </div>
              </div>
            </div>

            <div className="workspace-modal-content">
              {isLoading ? (
                <div className="workspace-loading">Loading files...</div>
              ) : error ? (
                <div className="workspace-error">{error}</div>
              ) : filteredAndSortedFiles.length === 0 ? (
                <div className="workspace-empty">
                  {searchQuery ? 'No files match your search' : 'No files found'}
                </div>
              ) : (
                <ul className="workspace-file-list">
                  {filteredAndSortedFiles.map((file) => (
                    <li 
                      key={file.id}
                      className="workspace-file-item"
                      onClick={() => handleItemClick(file)}
                    >
                      <div className="file-item-content">
                        <div className="file-item-main">
                          <div className="file-icon-wrapper">
                            {getFileIcon(file)}
                          </div>
                          <span className="file-name">{file.name || file.filename}</span>
                          {(file.is_folder || file.type === 'folder' || file.is_directory)}
                        </div>
                        <div className="file-item-actions">
                          {file.last_used && (
                            <span className="file-date">{formatDate(file.last_used)}</span>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
